// src/oura/merchant/Home.tsx
//
// Home: the merchant's landing view at /oura/dashboard (the view is still
// called `dashboard` in the router, `progress` inside ClientPage).
//
// With the redesign switch on (19 Oct 2026): the one thing that needs them
// beside their account manager (HomeTop.tsx), three numbers and each AI
// assistant (HomeNumbers.tsx), then this month's work (HomeMonth.tsx). Every
// piece of work, the 12 months and every request are on Our work.
//
// With it off: one sentence says how many things need them and how many are
// late; "Start answering" walks them in the drawer. Then three tiles, the
// latest note from their account manager, and a pair of cards: what needs
// their reply beside what comes next, or, with nothing waiting, what comes
// next beside the latest actions, so the page is never bare.
//
// The reader is a business owner on a phone between customers: one sentence,
// then numbers that each open something. Nothing here is dated from today.
//
// "Needs your reply" is the head of the one queue (docAsks.ts), questions and
// documents together, so the sentence, the card, the Decisions page and the
// nav badge agree. A row opens the queue at that item.

import { useNavigate } from "react-router-dom";
import { Fragment, useCallback, useRef, useState, type ReactNode } from "react";
import { CalendarDaysIcon, ClipboardListIcon, CompassIcon, FlagIcon, HistoryIcon, MessageCircleQuestionIcon, MessageSquareTextIcon, EyeIcon, UsersIcon } from "lucide-react";
import type { ClientPagePayload, Thread } from "@service/aeo/clientReview";
import {
    AREA_TEXT,
    ArrowLink,
    Bento,
    CardEmpty,
    FOCUS_RING,
    FoldCard,
    FoldScope,
    LATE_TEXT,
    ListRow,
    PageHead,
    PillButton,
    PillLink,
    Steps,
    Tag,
    Tile,
    Tiles,
    TYPE,
    type Tone,
} from "@oura/ui";
import { cn } from "../lib/cn";
import { dayDate, initials } from "../lib/oura";
import { CLIENT_HINT, OURA_ROOT, OURA_TEAM_ACCESS_PATH, showOnboarding } from "../nav";
import { clientArea, MERCHANT_V2, welcomeTitle } from "../merchantV2";
import { ActionRow } from "./ActionRow";
import { ACCOUNT_MANAGER_TITLE, askFact, type TimingPart } from "./asks";
import { completedText, docDue, fileTypeOf, isEntryLate, pendingCounts, pendingText, type QueueEntry } from "./docAsks";
import { setupPart, useMySetup } from "../hooks/useMySetup";
import { useAnswersEmptiedFocus } from "./drawers/answersEmptied";
import { DatesDrawer } from "./drawers/DatesDrawer";
import { PlanDrawer } from "./drawers/PlanDrawer";
import { DidDrawer } from "./drawers/DidDrawer";
import { VisitsDrawer } from "./drawers/VisitsDrawer";
import { comingUp, liveSlip, measuredItems, nextAiCheckDate, phaseOf, planSpanText, type ComingDate, type MerchantLive } from "./model";
import { foundFigure, visitsFigure, workFigure, type Figure } from "./homeFigures";
import { useWaitsForBrand } from "./brandWait";
import { MonthCard } from "./HomeMonth";
import { EnginesCard, NumberTiles } from "./HomeNumbers";
import { ManagerCard, NeedsYouCard } from "./HomeTop";
import { StartAnswering } from "./StartAnswering";
import { useMerchantDrawers } from "./useMerchantDrawers";
import { WelcomeCard } from "./WelcomeCard";
import { FirstRunHome } from "./FirstRun";
import { firstRunOf } from "./firstRunState";
import { startMerchantTour } from "./Tour";

/** Rows the Needs your reply card shows; Decisions has the rest. */
const QUEUE_SHOWN = 3;
/** Latest actions shown when nothing needs them; Work has the rest. */
const ACTIONS_SHOWN = 3;

/** A file-type tone as a 14px glyph's colour: the IconSquare's glyph, on white. */
const FILE_GLYPH: Partial<Record<Tone, string>> = {
    blue: "text-[#0A5FD1]",
    red: "text-[#C5221F]",
    green: "text-[#1E7B38]",
    gray: "text-gray-500",
};

type HomeDrawer = { kind: "dates" } | { kind: "plan"; line: string | null; n: number } | { kind: "visits" } | { kind: "did"; id: string } | null;

export function Home({
    payload,
    live,
}: {
    payload: ClientPagePayload;
    /**
     * The named count and the searches, read from the business detail beside
     * the client page. Undefined while that read is on its way, so the page
     * never waits for it; null when it could not be read.
     */
    live: MerchantLive | null | undefined;
}) {
    const { queue, readyDraftIds, openQueue, openQuestion } = useMerchantDrawers();
    const [drawer, setDrawer] = useState<HomeDrawer>(null);
    const navigate = useNavigate();
    const close = () => setDrawer(null);
    // Each opening remounts Your plan, so it opens at the line asked for with only that month unfolded.
    const opened = useRef(0);
    const openPlan = (line: string | null) => setDrawer({ kind: "plan", line, n: ++opened.current });
    const pageRef = useRef<HTMLDivElement>(null);

    // The queue ran out on Done: the rows it opened from have gone. Focus goes
    // to the Needs your reply card's head while it is still here, else to
    // the page title, which stays put.
    const focusSteady = useCallback(() => {
        const head = document.querySelector<HTMLElement>("#needs-reply h2 button");
        if (head?.isConnected && head.getClientRects().length > 0) {
            head.focus();
            return document.activeElement === head;
        }
        const h1 = pageRef.current?.querySelector<HTMLElement>("h1");
        if (!h1) return false;
        // The kit's title takes no tabIndex of its own; it can hold focus without joining the Tab order.
        if (!h1.hasAttribute("tabindex")) h1.setAttribute("tabindex", "-1");
        h1.focus();
        return document.activeElement === h1;
    }, []);
    useAnswersEmptiedFocus(focusSteady);

    const dates = comingUp(payload);
    const { stats, weekNote } = payload;
    // Nobody has been sold anything yet (#3710): there is no plan to count
    // against, and "0 of 0" would read as promised work not done.
    const planStarted = Boolean(payload.plan && payload.subscription && payload.plan.items.length > 0);
    // The plan lines that wait on the owner, counted as the month card counts them, so "Needs you" never
    // says "up to date" beside "2 need you" (#4388).
    const planWaiting = planStarted && payload.plan ? measuredItems(payload.plan.items).filter((i) => i.status === "waiting_on_you").length : 0;
    const setupRead = useMySetup();
    const setup = setupPart(setupRead);
    // Before the first AI check is ready, Home says what happens next instead of a page of dashes.
    const firstRun = MERCHANT_V2 ? firstRunOf(setupRead) : null;
    // Until the read answers, whether first run replaces the numbers is not known: draw neither (#4440).
    const firstRunPending = MERCHANT_V2 && setupRead.status === "loading";
    const waitsForBrand = useWaitsForBrand(true);
    const threadOf = (itemId: string) => payload.threads.find((t) => t.itemId === itemId) ?? null;
    const searches = stats.search?.topQueries ?? live?.searches ?? null;
    const didEntry = drawer?.kind === "did" ? payload.activity.find((a) => a.id === drawer.id) ?? null : null;

    // "6 pending, 4 late · 12 completed" (#4026 C-FE-6). It replaced
    // "Nothing needs you", which was not a description of anything and read
    // as an apology on an account that was simply up to date. Both figures
    // count the client's own open set-up steps as well as their asks, so this
    // line, the Decisions page and the Decisions badge agree -- and so does
    // the staff Overview's "Needs the client".
    const counts = pendingCounts(payload, setup);
    const pending = pendingText(counts);
    const sentence = (
        <>
            {!planStarted && "Your plan has not started yet. "}
            {pending.text}
            {pending.late && (
                <>
                    , <span className={LATE_TEXT}>{pending.late}</span>
                </>
            )}
            {" · "}
            {completedText(counts)}
        </>
    );

    const coming = <ComingUpCard dates={dates} onOpen={() => setDrawer({ kind: "dates" })} />;
    const found = foundFigure({
        live,
        aiCheck: stats.aiCheck,
        nextCheck: nextAiCheckDate(payload),
        scheduled: Boolean(payload.schedule),
        planStarted,
        waitsForBrand,
    });
    const work = workFigure({ payload, planStarted, next: dates[0] ?? null });
    const visits = visitsFigure(stats.search);
    const openVisits = () => setDrawer({ kind: "visits" });

    // The welcome card's own rule (it renders nothing once set-up is no longer theirs).
    const welcomeShown = MERCHANT_V2 && showOnboarding && !firstRun && setup.known && setup.mine > 0;

    return (
        <FoldScope value="merchant-home">
            <div ref={pageRef}>
                {/* The redesigned head is the business name and the month, then a
                    greeting -- no sentence (Jack, 18 Oct 2026): the figures are
                    on the cards below, where the thing they count is. When AI
                    last checked sits on the right. */}
                {MERCHANT_V2 ? (
                    <PageHead
                        over={[payload.business.name, payload.schedule && `Month ${payload.schedule.currentMonth} of ${payload.schedule.months.length}`].filter(Boolean).join(" · ")}
                        title={welcomeTitle(payload.viewer?.name, payload.business.name)}
                        actions={
                            <>
                                {/* Never before the first results are sent: no check date reaches the merchant until then. */}
                                {!firstRun && !firstRunPending && dayDate(live?.found?.checkedAt) && <span className={TYPE.lab}>Last checked {dayDate(live?.found?.checkedAt)}</span>}
                                {/* The tour's own button while the welcome card is gone
                                    (set-up done): the card carried "Show me around". */}
                                {showOnboarding && setup.known && setup.mine > 0 ? null : (
                                    <PillButton icon={CompassIcon} onClick={startMerchantTour}>
                                        Show me around
                                    </PillButton>
                                )}
                                {/* Inviting colleagues: on the welcome card while it is
                                    on Home, here once it has gone. */}
                                {welcomeShown ? null : (
                                    <PillLink to={OURA_TEAM_ACCESS_PATH} icon={UsersIcon}>
                                        Invite your team
                                    </PillLink>
                                )}
                            </>
                        }
                    />
                ) : (
                    <PageHead
                        over={<OverLine payload={payload} />}
                        title={payload.business.name}
                        // The fixed description (#4026 C-FE-6), the same sentence
                        // the rail's tooltip shows.
                        sentence={CLIENT_HINT.dashboard}
                        // Today's figures, which this line always was.
                        status={sentence}
                        actions={<StartAnswering />}
                    />
                )}
            </div>
            <div className="space-y-5">
                {/* Under the title, not above it, so Home opens like every
                    other page. It renders nothing once set-up is theirs no
                    longer. */}
                {MERCHANT_V2 && showOnboarding && !firstRun && <WelcomeCard />}
                {/* What waits on them beside everything from their account
                    manager, then the numbers, then this month (19 Oct 2026). */}
                {firstRunPending ? null : firstRun ? (
                    <FirstRunHome run={firstRun} payload={payload} />
                ) : MERCHANT_V2 ? (
                    <>
                        {/* Each card hugs its own content: stretched to the taller one, the
                            Needs you card opened an empty band above its buttons (2 Nov 2026). */}
                        <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-3 [&>*]:min-w-0">
                            <NeedsYouCard queue={queue} threadOf={threadOf} onOpen={openQueue} planWaiting={planWaiting} setupLeft={setup.known ? setup.mine : 0} />
                            <ManagerCard payload={payload} />
                        </div>
                        <NumberTiles found={found} visits={visits} windowDays={stats.search?.windowDays ?? null} onOpenVisits={stats.search ? openVisits : null} live={live} />
                        <EnginesCard engines={live === undefined ? undefined : live?.engines ?? null} weakest={live?.standing?.weakest?.key ?? null} />
                        <MonthCard payload={payload} dates={dates} onOpenDates={() => openPlan(null)} onOpenLine={(key) => navigate(`${OURA_ROOT}/work?line=${encodeURIComponent(key)}`)} />
                    </>
                ) : (
                    <Tiles cols={3} compactOnPhone>
                        <FoundTile figure={found} />
                        <ThisMonthTile figure={work} />
                        <VisitsTile figure={visits} onOpen={stats.search ? openVisits : undefined} />
                    </Tiles>
                )}
                {/* The redesign shows the note inside the account manager card, below. */}
                {!MERCHANT_V2 && weekNote && <NoteCard note={weekNote} preparedBy={payload.preparedBy} contactEmail={payload.contactEmail ?? null} />}
                {/* A pair, so neither column is bare: what needs them beside what
                    comes next, or, with nothing waiting, what comes next beside
                    what we did. */}
                {!MERCHANT_V2 && (
                <Bento layout="c2">
                    {queue.length > 0 ? (
                        <>
                            <NeedsReplyCard queue={queue} threadOf={threadOf} ready={readyDraftIds} onOpen={openQueue} />
                            {coming}
                        </>
                    ) : (
                        <>
                            {coming}
                            <LatestActionsCard activity={payload.activity} onOpen={(id) => setDrawer({ kind: "did", id })} />
                        </>
                    )}
                </Bento>
                )}
            </div>

            {MERCHANT_V2 && (
                <PlanDrawer
                    key={drawer?.kind === "plan" ? drawer.n : 0}
                    open={drawer?.kind === "plan"}
                    onClose={close}
                    payload={payload}
                    dates={dates}
                    openKey={drawer?.kind === "plan" ? drawer.line : null}
                    onAnswer={(askId) => {
                        close();
                        openQueue(askId);
                    }}
                />
            )}
            <DatesDrawer
                open={drawer?.kind === "dates"}
                onClose={close}
                dates={dates}
                term={planSpanText(payload)}
                changed={payload.schedule?.changed ?? null}
                onAnswer={(itemId) => {
                    close();
                    openQuestion(itemId);
                }}
            />
            {stats.search && (
                <VisitsDrawer
                    open={drawer?.kind === "visits"}
                    onClose={close}
                    search={stats.search}
                    searches={searches}
                    appearingFor={stats.appearingForAbsent ? [] : stats.appearingFor}
                />
            )}
            <DidDrawer key={didEntry?.id} entry={didEntry} onClose={close} />
        </FoldScope>
    );
}

/** "Growth [Stage 2] · Month 2 of 12 · Updated Sun 27 Sep": the date is the page's own, never today's. */
function OverLine({ payload }: { payload: ClientPagePayload }) {
    const phase = phaseOf(payload.plan);
    const termMonths = payload.subscription?.termMonths ?? 0;
    const updated = dayDate(payload.updatedAt);
    // Read as the rail reads it (useClientBadges' termOf): the schedule first,
    // whole months, inside the term, or nothing.
    const month = payload.schedule?.currentMonth ?? payload.plan?.month;
    const of = payload.schedule?.months?.length || termMonths;
    const term = month != null && Number.isInteger(month) && month >= 1 && month <= of ? `Month ${month} of ${of}` : null;
    const parts: ReactNode[] = [
        phase && (
            <span className="inline-flex items-center gap-1.5">
                {phase.label}
                <Tag tone={phase.tone}>{phase.stage}</Tag>
            </span>
        ),
        term,
        updated && `Updated ${updated}`,
    ].filter(Boolean);
    return (
        <>
            {/* Each part kept whole: a narrow line wraps between parts, never leaving "Sep" alone. */}
            {parts.map((part, i) => (
                <Fragment key={i}>
                    {i > 0 && " · "}
                    <span className="whitespace-nowrap">{part}</span>
                </Fragment>
            ))}
        </>
    );
}

/*
 * The tiles Home shows with the redesign switch off, as it did before the top
 * row: the same figures (homeFigures.tsx), each tile opening its detail.
 */

function FoundTile({ figure }: { figure: Figure }) {
    return (
        <Tile compactOnPhone
            label="How you're found"
            icon={{ icon: EyeIcon, area: clientArea("ai") }}
            to={`${OURA_ROOT}/questions`}
            value={figure.value}
            sub={figure.sub}
            loading={figure.loading}
            ariaLabel={figure.ariaLabel}
        />
    );
}

function ThisMonthTile({ figure }: { figure: Figure }) {
    return (
        <Tile compactOnPhone
            label="This month"
            icon={{ icon: ClipboardListIcon, area: clientArea("work") }}
            to={`${OURA_ROOT}/work`}
            value={figure.value}
            sub={figure.sub}
            ariaLabel={figure.ariaLabel}
        />
    );
}

/** Opens the searches behind the clicks, once Google reports any. */
function VisitsTile({ figure, onOpen }: { figure: Figure; onOpen?: () => void }) {
    return (
        <Tile compactOnPhone
            id="tour-tile-visits"
            label="Who came to you"
            icon={{ icon: UsersIcon, area: clientArea("google") }}
            value={figure.value}
            sub={figure.sub}
            onClick={onOpen}
            ariaLabel={onOpen ? figure.ariaLabel : undefined}
        />
    );
}

/**
 * The newest weekly note, at the top, from a person. Earlier notes stay with
 * staff. The payload drops the note's author today (BE handoff "Friday note
 * author on the merchant payload"), so until it ships the note is signed by
 * the account manager, who may not have written it, or by ourteam when
 * nobody is assigned. Both names come from the payload; none is written here.
 *
 * The account manager's address shows beside "Your account manager" only when
 * the payload carries one (`contactEmail`, BE handoff "Client email
 * reply-to"). On live today it does not, and no address is shown.
 */
function NoteCard({
    note,
    preparedBy,
    contactEmail,
}: {
    note: NonNullable<ClientPagePayload["weekNote"]>;
    preparedBy: string | null;
    contactEmail: string | null;
}) {
    const person = note.author ?? preparedBy;
    const manager = Boolean(person && person === preparedBy);
    return (
        <FoldCard
            id="week-note"
            // "Latest", never "This week" (staging f73b9933): the server sends
            // the newest note however old it is, and only today's date could
            // tell a week-old note from an older one, which no merchant screen
            // reads. The note's own date sits on the right.
            // "Latest from Angel": one line beside the date at 375px, where "Latest note from Angel" took two.
            title={`Latest from ${person ?? "ourteam"}`}
            icon={{ icon: MessageSquareTextIcon, area: clientArea("people") }}
            right={dayDate(note.date)}
        >
            <div className="flex items-start gap-3">
                {person && (
                    <span aria-hidden className={cn(TYPE.tc, "grid h-10 w-10 shrink-0 place-items-center rounded-full bg-blue-100 font-semibold text-blue-700")}>
                        {initials(person)}
                    </span>
                )}
                <div className="min-w-0 flex-1">
                    {manager && (
                        <p className={cn(TYPE.tm, "m-0 text-gray-500")}>
                            {ACCOUNT_MANAGER_TITLE}
                            {contactEmail && (
                                <>
                                    {" · "}
                                    <a href={`mailto:${contactEmail}`} className={cn("rounded font-medium text-blue-600 hover:underline", FOCUS_RING)}>
                                        {contactEmail}
                                    </a>
                                </>
                            )}
                        </p>
                    )}
                    <p className={cn(TYPE.tb, "m-0 mt-1 whitespace-pre-line leading-[22px] text-gray-900")}>{note.body}</p>
                </div>
            </div>
        </FoldCard>
    );
}

/** The one fact a queue entry carries: a question's, or a doc's date. */
function entryFact(e: QueueEntry, thread: Thread | null): TimingPart | null {
    return e.kind === "question" ? askFact(e.ask, thread) : docDue(e.doc);
}

/**
 * The head of the queue: the first 3 things that need them, questions and
 * documents together, most pressing first. A row opens the queue at it.
 */
function NeedsReplyCard({
    queue,
    threadOf,
    ready,
    onOpen,
}: {
    queue: QueueEntry[];
    threadOf: (itemId: string) => Thread | null;
    /** Questions with an answer written and not sent. */
    ready: ReadonlySet<string>;
    onOpen: (id: string) => void;
}) {
    const late = queue.filter(isEntryLate).length;
    const first = queue[0] ? entryFact(queue[0], queue[0].kind === "question" ? threadOf(queue[0].id) : null) : null;
    return (
        <FoldCard
            id="needs-reply"
            anchor="needs-reply"
            title="Needs your reply"
            icon={{ icon: FlagIcon, area: clientArea("asks") }}
            foldedFact={late > 0 ? <span className={LATE_TEXT}>{late} late</span> : first?.text}
        >
            <div>
                {queue.slice(0, QUEUE_SHOWN).map((e) => {
                    const fact = entryFact(e, e.kind === "question" ? threadOf(e.id) : null);
                    const type = e.kind === "doc" ? fileTypeOf(e.doc) : null;
                    const Icon = type ? type.icon : MessageCircleQuestionIcon;
                    const data = e.kind === "question" ? { "data-ask-id": e.id } : { "data-doc-id": e.id };
                    return (
                        <ListRow
                            key={`${e.kind}:${e.id}`}
                            {...data}
                            lead="icon"
                            stackMeta
                            icon={<Icon aria-hidden size={14} strokeWidth={2} className={type ? FILE_GLYPH[type.tone] ?? "text-gray-500" : AREA_TEXT.asks} />}
                            title={e.title}
                            srStatus={type ? type.label : undefined}
                            tags={e.kind === "question" && ready.has(e.id) ? <Tag tone="blue">Ready to send</Tag> : undefined}
                            meta={fact && !fact.late ? fact.text : undefined}
                            late={fact?.late ? fact.text : undefined}
                            onClick={() => onOpen(e.id)}
                        />
                    );
                })}
            </div>
            {queue.length > QUEUE_SHOWN && (
                <p className="m-0 mt-2">
                    {/* Named and counted: "See all 7 in To do" says where it
                        goes and how much is there (#4062 MR-FE-5). */}
                    <ArrowLink to={`${OURA_ROOT}/decisions`}>
                        {MERCHANT_V2 ? `See all ${queue.length} in To do` : "See all on Decisions"}
                    </ArrowLink>
                </p>
            )}
        </FoldCard>
    );
}

/**
 * The dates coming up. One report row: the rest are in the dates drawer,
 * with why any date moved. A date that moved and still stands to be
 * explained says what it was, in rose.
 */
function ComingUpCard({ dates, onOpen }: { dates: ComingDate[]; onOpen: () => void }) {
    const firstReport = dates.findIndex((d) => d.kind === "report");
    const shown = dates.filter((d, i) => d.kind !== "report" || i === firstReport);
    return (
        <FoldCard id="coming-up" title="Coming up" icon={{ icon: CalendarDaysIcon, area: clientArea("dates") }} foldedFact={dayDate(shown[0]?.date) ?? undefined}>
            {shown.length > 0 ? (
                <Steps
                    items={shown.map((d) => ({
                        key: d.key,
                        title: d.label,
                        meta: dayDate(d.date) ?? undefined,
                        late: liveSlip(d) ?? undefined,
                        state: "next",
                        onClick: onOpen,
                    }))}
                />
            ) : (
                <CardEmpty icon={CalendarDaysIcon} title="No dates yet">
                    Your report and check dates appear here once your plan starts.
                </CardEmpty>
            )}
        </FoldCard>
    );
}

/** With nothing waiting on them: the newest things we did, beside the dates. */
function LatestActionsCard({ activity, onOpen }: { activity: ClientPagePayload["activity"]; onOpen: (id: string) => void }) {
    const latest = dayDate(activity[0]?.at);
    const [all, setAll] = useState(false);
    return (
        <FoldCard id="actions" title="Latest actions" icon={{ icon: HistoryIcon, area: clientArea("work") }} foldedFact={latest ? `Latest ${latest}` : undefined}>
            {activity.length === 0 ? (
                <CardEmpty icon={HistoryIcon} title="No work logged yet">
                    Every piece of work we do for you shows up here as it happens.
                </CardEmpty>
            ) : (
                <>
                    <div>
                        {(MERCHANT_V2 && all ? activity : activity.slice(0, ACTIONS_SHOWN)).map((a) => (
                            <ActionRow key={a.id} entry={a} onOpen={() => onOpen(a.id)} />
                        ))}
                    </div>
                    {/* The rest opens HERE, rather than sending them to another
                        page for the same list (#4062 MR-FE-5). This card is the
                        proof that work is being done; it should not cost a
                        navigation to read it.
                        With the switch off, the link to Work is what it has
                        always been -- the flag has to hold in both directions
                        or it is not a rollout switch (Jack, #4066). */}
                    {!MERCHANT_V2 ? (
                        <p className="m-0 mt-2">
                            <ArrowLink to={`${OURA_ROOT}/work`}>See all on Work</ArrowLink>
                        </p>
                    ) : activity.length > ACTIONS_SHOWN ? (
                        <p className="m-0 mt-2">
                            <button
                                type="button"
                                onClick={() => setAll((v) => !v)}
                                className={cn("rounded text-sm font-medium text-blue-600 hover:underline", FOCUS_RING)}
                            >
                                {all ? "Show fewer" : `Show ${activity.length - ACTIONS_SHOWN} more`}
                            </button>
                        </p>
                    ) : null}
                </>
            )}
        </FoldCard>
    );
}
